<template>
  <header
    id="header"
    v-click-outside="() => (mobileNavMenu = false)"
    v-page-scroll="() => (mobileNavMenu = false)"
    class="header"
  >
    <div
      class="side-nav-btn"
      @click="toggleSideNav"
    >
      <div class="top-bar" />
      <div class="bottom-bar" />
    </div>

    <div
      v-if="organization"
      class="org-details-container"
    >
      <button
        type="button"
        class="org-logo-button"
        aria-label="Open organization details"
        @click="openOrganizationModal"
      >
        <OrganizationLogo
          v-if="hasConfiguredOrganizationLogo"
          alt="Organization Logo"
          class="org-logo"
          :fallback-src="organization.imageUrl || '/logo.png'"
        />
        <img
          v-else-if="organization.imageUrl"
          :src="organization.imageUrl"
          alt="Organization Logo"
          class="org-logo"
        >
        <p
          v-else
          class="org-name"
          :title="organization.name"
        >
          {{ truncatedOrganizationName }}
        </p>
      </button>
    </div>

    <div
      class="btn-hamburger"
      @click="mobileNavMenu = !mobileNavMenu"
    >
      <!-- use .btn-humburger-active to open the menu -->
      <div class="top bar" />
      <div class="middle bar" />
      <div class="bottom bar" />
    </div>

    <!-- use .navbar-open to open nav -->
    <nav
      class="navbar"
      :class="{ 'navbar-open': mobileNavMenu }"
    >
      <ul>
        <!-- <li>
          <NotificationsPanel />
        </li> -->
        <li
          v-if="getConfirmedUser"
          class="navbar-user"
        >
          <a
            v-click-outside="() => (userDropdownOpen = false)"
            @click.prevent="userDropdownOpen = !userDropdownOpen"
          >
            <AppAvatarImg
              v-if="authUser?.avatar"
              class="avatar-small"
              :src="authUser?.avatar"
              :alt="`profile picture`"
            />
            <AppAvatarImg
              v-else
              class="avatar-small"
              src="/user-placeholder.png"
              :alt="`profile picture`"
            />
            <span>
              <span v-if="authUser?.name">{{ authUser.name.first }} {{ authUser.name.last }}</span>
              <img
                class="icon-profile"
                src="../assets/svg/arrow-profile.svg"
                alt="Arrow"
                style="margin-left: 5px"
              >
            </span>
          </a>

          <!-- dropdown menu -->
          <!-- add class "active-drop" to show the dropdown -->
          <div
            id="user-dropdown"
            :class="{ 'active-drop': userDropdownOpen }"
          >
            <div class="triangle-drop" />
            <ul class="dropdown-menu">
              <li class="dropdown-menu-item">
                <router-link :to="{ name: 'Profile' }">
                  View Profile
                </router-link>
              </li>
              <li class="dropdown-menu-item">
                <a @click.prevent="signOut">Sign Out</a>
              </li>
            </ul>
          </div>
        </li>
        <li
          v-if="!getConfirmedUser"
          class="navbar-item"
        >
          <router-link :to="{ name: 'AuthLogin' }">
            Sign In
          </router-link>
        </li>
        <li
          v-if="getConfirmedUser"
          class="navbar-mobile-item"
        >
          <router-link :to="{ name: 'Profile' }">
            View Profile
          </router-link>
        </li>
        <li
          v-if="getConfirmedUser"
          class="navbar-mobile-item"
        >
          <a @click.prevent="signOut">Sign Out</a>
        </li>
      </ul>
    </nav>
    <div
      v-if="orgDetailsModalOpen && organization"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="organization-details-title"
      @click.self="closeOrganizationModal"
    >
      <div class="modal organization-modal">
        <div class="organization-modal-header">
          <h2
            id="organization-details-title"
            class="modal-title"
          >
            Organization Details
          </h2>
          <button
            type="button"
            class="organization-close-button"
            aria-label="Close organization details"
            @click="closeOrganizationModal"
          >
            X
          </button>
        </div>

        <div class="organization-modal-body">
          <OrganizationLogo
            v-if="hasConfiguredOrganizationLogo"
            :alt="organization.name || 'Organization Logo'"
            class="organization-modal-logo"
            :fallback-src="organization.imageUrl || '/logo.png'"
          />
          <img
            v-else-if="organization.imageUrl"
            :src="organization.imageUrl"
            :alt="organization.name || 'Organization Logo'"
            class="organization-modal-logo"
          >

          <ul class="organization-detail-list">
            <li>
              <span class="organization-detail-label">ID</span>
              <span>{{ organization.id || '-' }}</span>
            </li>
            <li>
              <span class="organization-detail-label">Name</span>
              <span>{{ organization.name || '-' }}</span>
            </li>
            <li>
              <span class="organization-detail-label">Email</span>
              <span>{{ organization.email || '-' }}</span>
            </li>
            <li>
              <span class="organization-detail-label">Phone</span>
              <span>{{ organization.phone || '-' }}</span>
            </li>
            <li>
              <span class="organization-detail-label">Address</span>
              <span>{{ organization.address || '-' }}</span>
            </li>
            <li>
              <span class="organization-detail-label">Website</span>
              <a
                v-if="normalizedWebsiteLink"
                :href="normalizedWebsiteLink"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ organization.website }}
              </a>
              <span v-else>-</span>
            </li>
            <li>
              <span class="organization-detail-label">Created At</span>
              <span>{{ formatDate(organization.createdAt) }}</span>
            </li>
            <li>
              <span class="organization-detail-label">Updated At</span>
              <span>{{ formatDate(organization.updatedAt) }}</span>
            </li>
          </ul>
        </div>

        <div
          v-if="canViewSubscriptionDetails"
          class="subscription-details-block"
        >
          <button
            class="subscription-toggle-btn beautiful-btn"
            :aria-expanded="showOrgSubscriptionDetails.toString()"
            aria-controls="org-subscription-details"
            type="button"
            @click="toggleOrgSubsciptionDetails"
          >
            <span class="icon-wrapper">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="2.5"
                  y="4.5"
                  width="15"
                  height="11"
                  rx="2"
                  stroke="#1769aa"
                  stroke-width="1.6"
                />
                <path
                  d="M2.5 8h15"
                  stroke="#1769aa"
                  stroke-width="1.6"
                />
                <circle
                  cx="6"
                  cy="12"
                  r="1.2"
                  fill="#1769aa"
                />
              </svg>
            </span>
            <span class="btn-text">Subscription details</span>
            <span
              class="chevron"
              :class="{ 'chevron-open': showOrgSubscriptionDetails }"
              aria-hidden="true"
            >
              ▼
            </span>
          </button>
          <transition name="fade-slide">
            <div
              v-if="showOrgSubscriptionDetails"
              id="org-subscription-details"
              class="subscription-details-panel"
            >
              <div
                v-if="subscriptionLoading"
                class="subscription-loading"
              >
                <span class="spinner" />
                <span class="loading-text">Loading subscription...</span>
              </div>
              <ul
                v-else
                class="organization-detail-list"
              >
                <li>
                  <span class="organization-detail-label">Subscription ID</span>
                  <span>{{ subscription.id || '-' }}</span>
                </li>
                <li>
                  <span class="organization-detail-label">Status</span>
                  <span>{{ capitalizeFirst(subscription.status) }}</span>
                </li>
                <li>
                  <span class="organization-detail-label">Plan Type</span>
                  <span>{{ capitalizeFirst(subscription.planType) }}</span>
                </li>
                <li>
                  <span class="organization-detail-label">Max Seats</span>
                  <span>{{ subscription.maxSeats || '-' }}</span>
                </li>
                <li>
                  <span class="organization-detail-label">Total Members</span>
                  <span>{{ subscription.total_members || '-' }}</span>
                </li>
                <li>
                  <span class="organization-detail-label">Active Members</span>
                  <span>{{ subscription.active_members || '-' }}</span>
                </li>
                <li>
                  <span class="organization-detail-label">Billing Cycle</span>
                  <span>{{ capitalizeFirst(subscription.billingCycle) }}</span>
                </li>
                <li>
                  <span class="organization-detail-label">Total CVR</span>
                  <span>{{ subscription.total_available_cvr !== undefined ? subscription.total_available_cvr : '-' }}</span>
                </li>
                <li>
                  <span class="organization-detail-label">Used Transactions</span>
                  <span>{{ subscription.usedTransactions !== undefined ? subscription.usedTransactions : '-' }}</span>
                </li>
                <li v-if="subscription.overage !== undefined && subscription.overage > 0">
                  <span class="organization-detail-label">Overage</span>
                  <span>{{ subscription.overage }}</span>
                </li>
                <li>
                  <span class="organization-detail-label">Start Date</span>
                  <span>{{ formatDate(subscription.startDate) || '-' }}</span>
                </li>
                <li>
                  <span class="organization-detail-label">End Date</span>
                  <span>{{ formatDate(subscription.endDate) || '-' }}</span>
                </li>
              </ul>
            </div>
          </transition>

          <button
            v-if="isOrgAdmin"
            type="button"
            class="api-key-btn beautiful-btn"
            :disabled="apiKeyCreationInProgress"
            @click="createApiKey"
          >
            <span
              class="icon-wrapper"
              aria-hidden="true"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M11.5 8.5a3 3 0 1 0-4.243-4.243L2.5 9.014a3.5 3.5 0 0 0 4.95 4.95l1.061-1.06"
                  stroke="#1769aa"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9.5 11.5a3 3 0 1 0 4.243 4.243l4.757-4.757a3.5 3.5 0 1 0-4.95-4.95l-1.06 1.06"
                  stroke="#1769aa"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span class="btn-text">{{ apiKeyCreationInProgress ? 'Creating API Key...' : 'Create API Key' }}</span>
          </button>

          <div
            v-if="isOrgAdmin"
            class="api-key-warning"
            role="alert"
          >
            Creating a new API key will invalidate your old key and break any deployment using it until the new key is updated.
          </div>

          <div
            v-if="createdApiKey"
            class="api-key-result-block"
          >
            <button
              type="button"
              class="api-key-result-toggle"
              :aria-expanded="showCreatedApiKeyDetails.toString()"
              aria-controls="org-created-api-key-details"
              @click="toggleCreatedApiKeyDetails"
            >
              <span
                class="icon-wrapper"
                aria-hidden="true"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <rect
                    x="3"
                    y="3"
                    width="14"
                    height="14"
                    rx="3"
                    stroke="#1769aa"
                    stroke-width="1.6"
                  />
                  <path
                    d="M7 10h6"
                    stroke="#1769aa"
                    stroke-width="1.6"
                    stroke-linecap="round"
                  />
                  <path
                    d="M10 7v6"
                    stroke="#1769aa"
                    stroke-width="1.6"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
              <span class="btn-text">Created API Key</span>
              <span
                class="chevron"
                :class="{ 'chevron-open': showCreatedApiKeyDetails }"
                aria-hidden="true"
              >
                ▼
              </span>
            </button>

            <transition name="fade-slide">
              <div
                v-if="showCreatedApiKeyDetails"
                id="org-created-api-key-details"
                class="subscription-details-panel api-key-result-panel"
              >
                <ul class="organization-detail-list">
                  <li>
                    <span class="organization-detail-label">ID</span>
                    <span>{{ createdApiKey.id || '-' }}</span>
                  </li>
                  <li>
                    <span class="organization-detail-label">Status</span>
                    <span>{{ capitalizeFirst(createdApiKey.status) }}</span>
                  </li>
                  <li>
                    <span class="organization-detail-label">Created At</span>
                    <span>{{ formatDate(createdApiKey.createdAt) }}</span>
                  </li>
                  <li>
                    <span class="organization-detail-label">Secret</span>
                    <div class="api-key-secret-row">
                      <span class="api-key-secret-value">{{ hideApiKeySecret ? maskApiKeySecret(createdApiKey.secret) :
                        (createdApiKey.secret || '-') }}</span>
                      <button
                        type="button"
                        class="api-key-inline-btn"
                        @click="toggleApiKeySecretVisibility"
                      >
                        {{ hideApiKeySecret ? 'Show' : 'Hide' }}
                      </button>
                      <button
                        type="button"
                        class="api-key-inline-btn"
                        :disabled="!createdApiKey.secret"
                        @click="copyApiKeySecret"
                      >
                        Copy
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </transition>
          </div>

          <div
            v-if="isOrgAdmin"
            class="api-key-result-block"
          >
            <button
              type="button"
              class="api-key-result-toggle"
              :aria-expanded="showRecentApiKeyDetails.toString()"
              aria-controls="org-current-api-key-details"
              @click="toggleRecentApiKeyDetails"
            >
              <span
                class="icon-wrapper"
                aria-hidden="true"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <rect
                    x="3"
                    y="3"
                    width="14"
                    height="14"
                    rx="3"
                    stroke="#1769aa"
                    stroke-width="1.6"
                  />
                  <path
                    d="M6.5 10h7"
                    stroke="#1769aa"
                    stroke-width="1.6"
                    stroke-linecap="round"
                  />
                  <path
                    d="M10 6.5v7"
                    stroke="#1769aa"
                    stroke-width="1.6"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
              <span class="btn-text">Current API Key Details</span>
              <span
                class="chevron"
                :class="{ 'chevron-open': showRecentApiKeyDetails }"
                aria-hidden="true"
              >
                ▼
              </span>
            </button>

            <transition name="fade-slide">
              <div
                v-if="showRecentApiKeyDetails"
                id="org-current-api-key-details"
                class="subscription-details-panel api-key-result-panel"
              >
                <div
                  v-if="recentApiKeyLoading"
                  class="subscription-loading"
                >
                  <span class="spinner" />
                  <span class="loading-text">Loading API key details...</span>
                </div>
                <ul
                  v-else-if="recentApiKey"
                  class="organization-detail-list"
                >
                  <li>
                    <span class="organization-detail-label">Hint</span>
                    <span>{{ recentApiKey.hint || '-' }}</span>
                  </li>
                  <li>
                    <span class="organization-detail-label">Status</span>
                    <span>{{ capitalizeFirst(recentApiKey.status) }}</span>
                  </li>
                </ul>
                <p
                  v-else
                  class="api-key-empty-state"
                >
                  No current API key details found.
                </p>
              </div>
            </transition>
          </div>
        </div>

        <div class="modal-buttons">
          <button
            type="button"
            class="cancel-button"
            @click="closeOrganizationModal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import useNotifications from '@/composables/useNotifications'
import logger from '@/services/logger'
import { organizationBranding } from '@/services/organizationBranding'
// import NotificationsPanel from './events/notificationsPanel.vue'
export default {
  // components: { NotificationsPanel },
  emits: ['sidenav-toggle'],
  data () {
    return {
      userDropdownOpen: false,
      mobileNavMenu: false,
      sideNavMenu: false,
      organization: null,
      subscription: null,
      subscriptionLoading: false,
      subscriptionCache: null,
      recentApiKey: null,
      recentApiKeyLoading: false,
      orgDetailsModalOpen: false,
      showOrgSubscriptionDetails: false,
      showRecentApiKeyDetails: false,
      apiKeyCreationInProgress: false,
      apiKeyIdempotencyKey: null,
      createdApiKey: null,
      showCreatedApiKeyDetails: true,
      hideApiKeySecret: true,
      apiKeyRequestController: null,
      addNotification: useNotifications().addNotification
    }
  },
  computed: {
    ...mapGetters('auth', ['getConfirmedUser']),
    ...mapGetters('auth', ['authUser']),
    ...mapGetters('authorization', ['isOrgAdmin']),
    ...mapState('authorization', ['organizationId', 'userRole']),
    canViewSubscriptionDetails () {
      return this.userRole === 'customer_owner'
    },
    truncatedOrganizationName () {
      const orgName = this.organization?.name || ''
      return orgName.length > 30 ? orgName.slice(0, 30) : orgName
    },
    hasConfiguredOrganizationLogo () {
      return Boolean(organizationBranding.logoUrl)
    },
    normalizedWebsiteLink () {
      if (!this.organization?.website) return ''

      const website = this.organization.website.trim()
      if (/^https?:\/\//i.test(website)) return website
      return `https://${website}`
    }
  },
  watch: {
    orgDetailsModalOpen (val) {
      if (val) {
        if (window && typeof window.disableBodyScroll === 'function') window.disableBodyScroll()
      } else {
        if (window && typeof window.enableBodyScroll === 'function') window.enableBodyScroll()
      }
    },
    organizationId: {
      handler (val) {
        if (val) {
          this.fetchOrganizationDetails()
          return
        }

        this.organization = null
        this.orgDetailsModalOpen = false
      },
      immediate: true
    }
  },
  created () {
    this.$router.beforeEach(() => {
      this.mobileNavMenu = false
    })
  },
  methods: {
    async signOut () {
      await this.$store.dispatch('auth/signOut')
      await this.$router.replace({ name: 'AuthLogin' })
    },
    toggleSideNav () {
      this.sideNavMenu = !this.sideNavMenu
      this.$emit('sidenav-toggle', this.sideNavMenu)
    },
    openOrganizationModal () {
      this.orgDetailsModalOpen = true
    },
    closeOrganizationModal () {
      if (this.apiKeyRequestController) {
        this.apiKeyRequestController.abort()
      }

      this.orgDetailsModalOpen = false
      this.showOrgSubscriptionDetails = false
      this.subscriptionCache = null
      this.subscription = null
      this.recentApiKey = null
      this.recentApiKeyLoading = false
      this.showRecentApiKeyDetails = false
      this.apiKeyCreationInProgress = false
      this.apiKeyIdempotencyKey = null
      this.createdApiKey = null
      this.showCreatedApiKeyDetails = true
      this.hideApiKeySecret = true
      this.apiKeyRequestController = null
    },
    toggleOrgSubsciptionDetails () {
      this.showOrgSubscriptionDetails = !this.showOrgSubscriptionDetails
      if (this.showOrgSubscriptionDetails) {
        // Use cache if available
        if (this.subscriptionCache && this.subscriptionCache.id) {
          this.subscription = this.subscriptionCache
          this.subscriptionLoading = false
        } else {
          this.fetchOrganizationSubscriptionDetails()
        }
      }
    },
    formatDate (dateString) {
      if (!dateString) return '-'

      const parsedDate = new Date(dateString)
      if (Number.isNaN(parsedDate.getTime())) return '-'

      return parsedDate.toLocaleString()
    },
    capitalizeFirst (value) {
      if (!value) return '-'

      const text = String(value).trim()
      if (!text) return '-'

      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    toggleCreatedApiKeyDetails () {
      this.showCreatedApiKeyDetails = !this.showCreatedApiKeyDetails
    },
    async toggleRecentApiKeyDetails () {
      const willOpen = !this.showRecentApiKeyDetails
      this.showRecentApiKeyDetails = willOpen

      if (!willOpen || this.recentApiKeyLoading) {
        return
      }

      await this.fetchRecentApiKeyDetails()
    },
    toggleApiKeySecretVisibility () {
      this.hideApiKeySecret = !this.hideApiKeySecret
    },
    maskApiKeySecret (secret) {
      if (!secret) return '-'

      if (secret.length <= 8) {
        return `${secret.slice(0, 2)}****${secret.slice(-2)}`
      }

      return `${secret.slice(0, 4)}${'*'.repeat(secret.length - 8)}${secret.slice(-4)}`
    },
    async copyApiKeySecret () {
      if (!this.createdApiKey?.secret) return

      try {
        await navigator.clipboard.writeText(this.createdApiKey.secret)
        this.addNotification({
          message: 'API key secret copied to clipboard.',
          timeout: 3000,
          type: 'success'
        })
      } catch (error) {
        logger.error('Error copying API key secret', { error: error.message, stack: error.stack })
        this.addNotification({
          message: 'Failed to copy API key secret.',
          timeout: 3000,
          type: 'error'
        })
      }
    },
    async createApiKey () {
      if (this.apiKeyCreationInProgress) return

      const token = window.sessionStorage.getItem('ACCESS_TOKEN')
      const configuredUrl = process.env.VUE_APP_BACKEND_BASE_URL
      const baseUrl = process.env.NODE_ENV === 'development'
        ? ''
        : (configuredUrl === '' ? '' : (configuredUrl || 'http://localhost:3005'))
      const url = baseUrl ? `${baseUrl}/api/organization/api-keys` : '/api/organization/api-keys'
      const idempotencyKey = this.apiKeyIdempotencyKey || uuidv4()

      this.apiKeyIdempotencyKey = idempotencyKey
      this.apiKeyCreationInProgress = true

      const headers = {
        'content-type': 'application/json',
        platform: 'UCP',
        'X-Client-Type': 'web',
        'Idempotency-Key': idempotencyKey,
        ...(token && { Authorization: `Bearer ${token}` })
      }

      let created = false

      while (this.orgDetailsModalOpen && !created) {
        let timeoutId = null
        try {
          const controller = new AbortController()
          this.apiKeyRequestController = controller
          timeoutId = setTimeout(() => controller.abort(), 10000)

          const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({}),
            signal: controller.signal
          })

          clearTimeout(timeoutId)

          if (!response.ok) {
            let errorMessage = 'Failed to create API key.'
            try {
              const errorBody = await response.clone().json()
              if (errorBody?.error) {
                errorMessage = errorBody.error
              }
            } catch (e) {
              // No-op: fallback message is already set
            }
            throw new Error(errorMessage)
          }

          const text = await response.text()
          const payload = text ? JSON.parse(text) : {}

          if (!payload?.id) {
            throw new Error('No response payload received from API key endpoint.')
          }

          this.createdApiKey = payload
          this.recentApiKey = payload
          this.hideApiKeySecret = true
          this.showCreatedApiKeyDetails = true
          created = true

          this.addNotification({
            message: 'API key created successfully.',
            timeout: 3000,
            type: 'success'
          })

          this.apiKeyCreationInProgress = false
          this.apiKeyRequestController = null
          return
        } catch (error) {
          logger.error('Error creating API key', { error: error.message, stack: error.stack })

          if (!this.orgDetailsModalOpen) {
            break
          }

          await new Promise(resolve => setTimeout(resolve, 1500))
        } finally {
          if (timeoutId) clearTimeout(timeoutId)
        }
      }

      this.apiKeyCreationInProgress = false
      this.apiKeyRequestController = null
    },
    async fetchOrganizationDetails () {
      try {
        const response = await this.$store.dispatch('fetchData', {
          stream: `organization/${encodeURIComponent(this.organizationId)}`
        }, { root: true })

        if (!response?.id) {
          this.addNotification({
            message: 'Failed to fetch organization details.',
            timeout: 3000,
            type: 'error'
          })
          this.organization = null
          this.orgDetailsModalOpen = false
          return
        }

        this.organization = response
      } catch (error) {
        logger.error('Error fetching organization details', { error: error.message, stack: error.stack })
        this.addNotification({
          message: 'Failed to fetch organization details.',
          timeout: 3000,
          type: 'error'
        })
        this.organization = null
        this.orgDetailsModalOpen = false
      }
    },
    async fetchOrganizationSubscriptionDetails () {
      this.subscriptionLoading = true
      try {
        const response = await this.$store.dispatch('fetchData', {
          stream: `organizations/${encodeURIComponent(this.organizationId)}/subscription`
        }, { root: true })

        if (!response?.subscription?.id) {
          this.addNotification({
            message: 'Failed to fetch organization subscription details.',
            timeout: 3000,
            type: 'error'
          })
          this.subscription = null
          this.subscriptionCache = null
          this.subscriptionLoading = false
          this.showOrgSubscriptionDetails = false
          return
        }

        this.subscription = response?.subscription
        this.subscription = {
          ...this.subscription,
          overage: this.subscription.usedTransactions - this.subscription.total_available_cvr
        }
        this.subscription.total_members = response?.total_members || 0
        this.subscription.active_members = response?.active_members || 0
        this.subscriptionCache = response?.subscription // cache it
        this.subscriptionLoading = false
      } catch (error) {
        logger.error('Error fetching organization subscription details', { error: error.message, stack: error.stack })
        this.addNotification({
          message: 'Failed to fetch organization subscription details.',
          timeout: 3000,
          type: 'error'
        })
        this.organization = null
        this.orgDetailsModalOpen = false
        this.subscriptionLoading = false
      }
    },

    async fetchRecentApiKeyDetails () {
      this.recentApiKeyLoading = true
      try {
        const response = await this.$store.dispatch('fetchData', {
          stream: 'organization/api-keys'
        }, { root: true })

        if (!response?.status) {
          this.addNotification({
            message: 'Failed to fetch organization api key details.',
            timeout: 3000,
            type: 'error'
          })
          this.recentApiKey = null
          return
        }

        this.recentApiKey = response
      } catch (error) {
        logger.error('Error fetching organization api key details', { error: error.message, stack: error.stack })
        this.addNotification({
          message: 'Failed to fetch organization organization api key details.',
          timeout: 3000,
          type: 'error'
        })
        this.recentApiKey = null
      } finally {
        this.recentApiKeyLoading = false
      }
    }
  }
}
</script>

<style scoped>
.menu-icon-holder {
  width: 20%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
}

.menu-icon img {
  width: 30px;
  height: 30px;
}

.org-details-container .org-logo {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.org-logo-button {
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.organization-modal {
  width: min(92vw, 640px);
  text-align: left;
  max-height: min(88vh, 720px);
  overflow-y: auto;
  background-color: white;
}

.organization-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.organization-close-button {
  border: 0;
  background: transparent;
  color: #545454;
  font-size: 16px;
  font-weight: 700;
  min-width: 36px;
  min-height: 36px;
  line-height: 1;
  cursor: pointer;
}

.organization-modal-body {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.organization-modal-logo {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.organization-detail-list {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
}

.organization-detail-list li {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 10px;
  margin-bottom: 8px;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.organization-detail-label {
  font-weight: 600;
  color: #545454;
}

.organization-detail-list a {
  color: var(--primary-color);
}

@media (max-width: 720px) {
  .modal-overlay {
    padding: 10px;
    align-items: flex-end;
  }

  .organization-modal {
    width: 100%;
    max-height: 84vh;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    padding: 16px;
  }

  .organization-modal-header {
    margin-bottom: 8px;
  }

  .organization-modal-body {
    flex-direction: column;
    gap: 10px;
    margin-bottom: 12px;
  }

  .organization-detail-list li {
    grid-template-columns: 1fr;
    gap: 4px;
    margin-bottom: 10px;
  }
}

@media (max-width: 420px) {
  .org-details-container .org-logo {
    width: 40px;
    height: 40px;
  }

  .organization-close-button {
    min-width: 32px;
    min-height: 32px;
  }
}

.org-details-container .org-logo {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.org-logo-button {
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.organization-modal {
  width: min(92vw, 640px);
  text-align: left;
  max-height: min(88vh, 720px);
  overflow-y: auto;
  background-color: white;
}

.organization-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.organization-close-button {
  border: 0;
  background: transparent;
  color: #545454;
  font-size: 16px;
  font-weight: 700;
  min-width: 36px;
  min-height: 36px;
  line-height: 1;
  cursor: pointer;
}

.organization-modal-body {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.organization-modal-logo {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.organization-detail-list {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
}

.organization-detail-list li {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 10px;
  margin-bottom: 8px;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.organization-detail-label {
  font-weight: 600;
  color: #545454;
}

.organization-detail-list a {
  color: var(--primary-color);
}

.org-name {
  max-width: 220px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 720px) {
  .modal-overlay {
    padding: 10px;
    align-items: flex-end;
  }

  .organization-modal {
    width: 100%;
    max-height: 84vh;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    padding: 16px;
  }

  .organization-modal-header {
    margin-bottom: 8px;
  }

  .organization-modal-body {
    flex-direction: column;
    gap: 10px;
    margin-bottom: 12px;
  }

  .organization-detail-list li {
    grid-template-columns: 1fr;
    gap: 4px;
    margin-bottom: 10px;
  }

  .org-name {
    margin-left: 8px;
  }
}

@media (max-width: 420px) {
  .org-details-container .org-logo {
    width: 40px;
    height: 40px;
  }

  .organization-close-button {
    min-width: 32px;
    min-height: 32px;
  }
}

.side-nav-btn {
  width: 20px;
  height: 20px;
  position: relative;
  cursor: pointer;
  display: none;
}

.top-bar,
.bottom-bar {
  position: absolute;
  width: 18px;
  height: 4px;
  background: #fff;
  right: 0;
  transform-origin: right center;
}

.top-bar {
  top: 9px;
  transform: rotate(45deg);
}

.bottom-bar {
  bottom: 9px;
  transform: rotate(-45deg);
}

@media (max-width: 965px) {
  .side-nav-btn {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}

/* Subscription details block styles */
.subscription-details-block {
  margin-top: 18px;
  padding: 14px;
  border: 1px solid #e1ebf6;
  border-radius: 14px;
  background: linear-gradient(180deg, #fbfdff 0%, #f3f8fe 100%);
}

.subscription-toggle-btn {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: transform 0.15s ease, color 0.2s ease;
}

.beautiful-btn {
  background: linear-gradient(90deg, #dff0ff 0%, #eef7ff 40%, #f8fbff 100%);
  border-radius: 12px;
  border: 1px solid #bfdcf8;
  min-height: 50px;
  padding: 10px 12px;
  box-shadow: 0 6px 18px rgba(31, 95, 158, 0.12);
}

.beautiful-btn:hover {
  transform: translateY(-1px);
  border-color: #7eb8ee;
  box-shadow: 0 10px 20px rgba(31, 95, 158, 0.16);
}

.icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: inset 0 0 0 1px rgba(111, 170, 224, 0.26);
}

.btn-text {
  flex: 1;
  text-align: left;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #155c98;
}

.chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.78);
  color: #155c98;
  transition: transform 0.3s cubic-bezier(.4, 2, .6, 1);
}

.chevron-open {
  transform: rotate(180deg);
}

.subscription-details-panel {
  margin-top: 12px;
  padding: 14px 14px 8px;
  background: #ffffff;
  border: 1px solid #d9e7f6;
  border-radius: 12px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95);
}

.api-key-btn {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  cursor: pointer;
}

.api-key-warning {
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid #f2c47d;
  border-radius: 8px;
  background: #fff8ec;
  color: #7a4a00;
  font-size: 14px;
  line-height: 1.4;
}

.api-key-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.api-key-result-block {
  margin-top: 10px;
  border: 1px solid #dbe8f5;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(21, 92, 152, 0.06);
  overflow: hidden;
}

.api-key-result-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 0;
  border-bottom: 1px solid #e6eef8;
  background: linear-gradient(180deg, #f7fbff 0%, #f1f7ff 100%);
  padding: 10px 12px;
  cursor: pointer;
}

.api-key-result-toggle .btn-text {
  color: #1c4f7a;
  font-size: 14px;
}

.api-key-result-toggle .icon-wrapper {
  background: #ffffff;
}

.api-key-result-panel {
  margin-top: 0;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.api-key-secret-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.api-key-secret-value {
  font-family: monospace;
  font-size: 13px;
  line-height: 1.5;
  background: #f5f9ff;
  border: 1px solid #d8e8fb;
  border-radius: 6px;
  padding: 4px 8px;
}

.api-key-inline-btn {
  border: 1px solid #bfdcf8;
  background: #ffffff;
  color: #155c98;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 6px 8px;
  cursor: pointer;
}

.api-key-inline-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.api-key-empty-state {
  margin: 0;
  color: #5b6f82;
  font-size: 14px;
  line-height: 1.5;
}

.subscription-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  justify-content: center;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 3px solid #d0e2ff;
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #1976d2;
  font-weight: 600;
  font-size: 15px;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(.4, 2, .6, 1);
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 720px) {
  .subscription-details-block {
    padding: 10px;
    margin-top: 12px;
  }

  .beautiful-btn {
    min-height: 46px;
    padding: 8px 10px;
  }

  .btn-text {
    font-size: 14px;
  }
}
/* Organization detail panels use the same tenant theme as the application. */
:global(:root[data-organization-branding="true"]) .organization-modal,
:global(:root[data-organization-branding="true"]) .subscription-details-panel,
:global(:root[data-organization-branding="true"]) .api-key-result-panel {
  background: var(--surface-color, #ffffff);
  color: var(--text-color, #333333);
  border-color: var(--border-color, transparent);
}

:global(:root[data-organization-branding="true"]) .organization-modal-header,
:global(:root[data-organization-branding="true"]) .organization-modal-body,
:global(:root[data-organization-branding="true"]) .organization-detail-list,
:global(:root[data-organization-branding="true"]) .organization-detail-list li {
  color: var(--text-color, #333333);
}

:global(:root[data-organization-branding="true"]) .organization-close-button,
:global(:root[data-organization-branding="true"]) .organization-detail-label,
:global(:root[data-organization-branding="true"]) .api-key-empty-state {
  color: var(--muted-text-color, #545454);
}

:global(:root[data-organization-branding="true"]) .organization-detail-list a,
:global(:root[data-organization-branding="true"]) .subscription-toggle-btn,
:global(:root[data-organization-branding="true"]) .api-key-inline-btn {
  color: var(--primary-color, #570F8B);
}

:global(:root[data-organization-branding="true"]) .subscription-toggle-btn.beautiful-btn,
:global(:root[data-organization-branding="true"]) .api-key-btn.beautiful-btn,
:global(:root[data-organization-branding="true"]) .api-key-result-toggle {
  background: var(--surface-color, #ffffff);
  color: var(--text-color, #333333);
  border: 1px solid var(--border-color, #d8e8fb);
  box-shadow: none;
}

:global(:root[data-organization-branding="true"]) .subscription-toggle-btn.beautiful-btn .btn-text,
:global(:root[data-organization-branding="true"]) .subscription-toggle-btn.beautiful-btn .chevron,
:global(:root[data-organization-branding="true"]) .api-key-btn.beautiful-btn .btn-text,
:global(:root[data-organization-branding="true"]) .api-key-btn.beautiful-btn .chevron,
:global(:root[data-organization-branding="true"]) .api-key-result-toggle .btn-text {
  color: var(--text-color, #333333);
}

:global(:root[data-organization-branding="true"]) .subscription-toggle-btn.beautiful-btn .icon-wrapper,
:global(:root[data-organization-branding="true"]) .api-key-btn.beautiful-btn .icon-wrapper,
:global(:root[data-organization-branding="true"]) .api-key-result-toggle .icon-wrapper {
  background: var(--surface-color, #ffffff);
  box-shadow: inset 0 0 0 1px var(--border-color, #d8e8fb);
  color: var(--primary-color, #570F8B);
}

:global(:root[data-organization-branding="true"]) .subscription-toggle-btn.beautiful-btn svg path,
:global(:root[data-organization-branding="true"]) .api-key-btn.beautiful-btn svg path,
:global(:root[data-organization-branding="true"]) .api-key-result-toggle svg path,
:global(:root[data-organization-branding="true"]) .api-key-result-toggle svg rect {
  fill: none !important;
  stroke: currentColor !important;
}

:global(:root[data-organization-branding="true"]) .subscription-toggle-btn.beautiful-btn svg circle,
:global(:root[data-organization-branding="true"]) .api-key-btn.beautiful-btn svg circle,
:global(:root[data-organization-branding="true"]) .api-key-result-toggle svg circle {
  fill: currentColor !important;
}

:global(:root[data-organization-branding="true"]) .api-key-result-toggle,
:global(:root[data-organization-branding="true"]) .api-key-secret-value {
  background: var(--surface-color, #ffffff);
  color: var(--text-color, #333333);
  border-color: var(--border-color, #d8e8fb);
}

:global(:root[data-organization-branding="true"]) .api-key-warning {
  background: var(--surface-color, #fff8ec);
  color: var(--text-color, #7a4a00);
  border-color: var(--border-color, #f2c47d);
}
</style>
